import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, IconButton } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import CustomButton from '../buttons/CustomButton';
import AttachmentThumb from '../patient-attachments/AttachmentThumb';
import AttachmentViewerPopup from '../patient-attachments/AttachmentViewerPopup';
import { getKindIcon, getKindInfo, isImageKind } from '../patient-attachments/attachmentKinds';
import { prepareUploadItem, releaseUploadItem } from '../../popup/PatientAttachmentUploadPopup/uploadAttachments';
import { DataProviderInstance } from '../../api/DataProvider';
import styles from './styles.module.css';

const ACCEPT = 'image/*,application/pdf';
const THUMB_SIZE = 40;

/**
 * Campo "Anexar" dentro do formulário de um documento de convênio: mostra os
 * anexos já gravados (na edição, só para ver) e os arquivos escolhidos agora.
 * O envio é de quem usa o campo, que só tem o id do documento depois de salvar
 * (mesmo padrão das fotos do popup de medidas da assimetria craniana).
 *
 * @param {number} patientId
 * @param {string} context MEDICAL_REQUEST_CONTEXT ou INSURANCE_AUTHORIZATION_CONTEXT
 * @param {number} contextId id do documento já gravado (ausente no "Novo")
 * @param {Array} items arquivos escolhidos agora (itens de prepareUploadItem)
 * @param {function} onItemsChange (items) => void
 * @param {boolean} disabled durante o envio
 * @param {number} maxFiles limite por envio
 * @param {function} onError (mensagem) => void
 */
function InsuranceDocumentFilesField({
  patientId, context, contextId, items, onItemsChange, disabled = false, maxFiles = 5, onError,
}) {
  const { ListPatientAttachments } = DataProviderInstance();

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const nextId = useRef(1);

  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [selected, setSelected] = useState(null);

  const hasContextId = contextId !== undefined && contextId !== null && contextId !== '';
  const chosen = items || [];

  useEffect(() => {
    if (!hasContextId || patientId === undefined || patientId === null) return;
    setLoading(true);
    ListPatientAttachments(patientId, context, (response) => {
      if (response && response.status === 'SUCCESS') {
        setExisting((response.data || []).filter((item) => String(item.contextId) === String(contextId)));
      }
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, context, contextId]);

  async function filesChosenHandler(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;

    const room = maxFiles - chosen.length;
    if (room <= 0) {
      if (onError) onError(`Até ${maxFiles} arquivos por envio.`);
      return;
    }
    const accepted = files.slice(0, room);
    if (accepted.length < files.length && onError) onError(`Até ${maxFiles} arquivos por envio.`);

    setPreparing(true);
    try {
      const prepared = [];
      for (const file of accepted) {
        try {
          prepared.push(await prepareUploadItem(file, nextId.current++));
        } catch (e) {
          if (onError) onError(`Não foi possível preparar "${file.name}".`);
        }
      }
      onItemsChange(chosen.concat(prepared));
    } finally {
      setPreparing(false);
    }
  }

  const removeItem = (id) => onItemsChange(chosen.filter((item) => {
    if (item.id === id) releaseUploadItem(item);
    return item.id !== id;
  }));

  const preview = (item) => {
    if (isImageKind(item.kind) && item.previewUrl) {
      return (
        <span className={styles.fileThumb}>
          <img src={item.previewUrl} alt={item.name} />
        </span>
      );
    }
    return (
      <span className={styles.fileThumb} style={{ backgroundColor: getKindInfo(item.kind).background }}>
        {getKindIcon(item.kind, 20)}
      </span>
    );
  };

  const busy = disabled || preparing;
  const chooseLabel = existing.length > 0 || chosen.length > 0 ? 'Anexar outro' : 'Escolher arquivo';

  return (
    <div className={styles.filesField}>
      {loading && <div className={styles.filesHint}><CircularProgress size={14} /> Carregando anexos…</div>}

      {existing.length > 0 && (
        <ul className={styles.fileList} aria-label="Anexos atuais">
          {existing.map((attachment) => (
            <li key={attachment.id} className={styles.fileRow}>
              <AttachmentThumb attachment={attachment} size={THUMB_SIZE} onClick={() => setSelected(attachment)} />
              <button type="button" className={styles.fileName} onClick={() => setSelected(attachment)}>
                {attachment.originalName || attachment.title || 'Anexo'}
              </button>
              <span className={styles.fileBadge}>Atual</span>
            </li>
          ))}
        </ul>
      )}

      {chosen.length > 0 && (
        <ul className={styles.fileList} aria-label="Arquivos a enviar">
          {chosen.map((item) => (
            <li key={item.id} className={styles.fileRow}>
              {preview(item)}
              <span className={styles.fileName} title={item.name}>{item.name}</span>
              <span className={`${styles.fileBadge} ${styles.fileBadgeNew}`}>Novo</span>
              <IconButton size="small" onClick={() => removeItem(item.id)} disabled={disabled} aria-label={`Remover ${item.name}`}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.filesActions}>
        <CustomButton
          variant="outlined"
          style="primary"
          label="Tirar foto"
          aria-label="Tirar foto"
          icon={<PhotoCameraOutlinedIcon />}
          onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
          enabled={!busy}
        />
        <CustomButton
          variant="outlined"
          style="primary"
          label={chooseLabel}
          aria-label={chooseLabel}
          icon={<AttachFileOutlinedIcon />}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          enabled={!busy}
        />
        {preparing && <span className={styles.filesHint}><CircularProgress size={14} /> Preparando…</span>}
      </div>
      <span className={styles.filesHint}>Foto ou PDF. O arquivo é enviado ao salvar.</span>

      <input ref={fileInputRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }} onChange={filesChosenHandler} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={filesChosenHandler} />

      {selected && (
        <AttachmentViewerPopup
          attachment={selected}
          list={existing}
          enabled={false}
          OnHide={() => setSelected(null)}
        />
      )}
    </div>
  );
}

export default InsuranceDocumentFilesField;
